import { useFragment, useMutation } from '@apollo/client/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Form, FormField, FormItem, FormControl } from '@/components/ui/form';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { AppMembershipFragment } from 'src/graphql/actions/appMemberships/appMemberships.fragment';
import { AppMembershipUpdateDocument } from 'src/graphql/actions/appMemberships/appMembershipUpdate.mutation';
import { AppMembershipRoleEnum, type IAppMembership } from 'src/graphql/schema';
import { useApp } from 'src/context/app';

import { useMembershipActions } from '../model';

const permissionOptions = [
  {
    value: AppMembershipRoleEnum.Manager,
    label: 'Manager',
    description: 'Can manage — details, members, products and agreements across this application.',
  },
  {
    value: AppMembershipRoleEnum.Viewer,
    label: 'Viewer',
    description: 'Can view — read-only access to everything in this application.',
  },
];

const schema = z.object({
  role: z.nativeEnum(AppMembershipRoleEnum),
});

type IFormValues = z.infer<typeof schema>;

type FormProps = {
  membershipId: string;
  role: AppMembershipRoleEnum;
};

const EditPermissionForm = ({ membershipId, role }: FormProps) => {
  const { closeEditDialog } = useMembershipActions();
  const [updateAppMembership, { loading }] = useMutation(AppMembershipUpdateDocument);

  const form = useForm<IFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { role },
  });

  const onSave = async ({ role }: IFormValues) => {
    try {
      const { data } = await updateAppMembership({
        variables: {
          input: {
            appMembershipId: membershipId,
            appMembershipParams: { role },
          },
        },
      });

      closeEditDialog();
      toast.success(`${data?.appMembershipUpdate?.appMembership?.user.name} is now a ${role}`);
    } catch (error) {
      toast.error('Failed to update member access');
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSave)} className="space-y-2">
        <FormField
          control={form.control}
          name="role"
          render={({ field }) => (
            <FormItem>
              <Label className="font-semibold text-slate-700">Permission</Label>
              <FormControl>
                <RadioGroup value={field.value} onValueChange={field.onChange}>
                  {permissionOptions.map((option) => (
                    <div key={option.value} className="flex items-start gap-2">
                      <RadioGroupItem value={option.value} id={`${membershipId}-${option.value}`} />
                      <Label htmlFor={`${membershipId}-${option.value}`} className="space-y-1.5">
                        <span className="block font-bold">{option.label}</span>
                        <p className="text-xs text-muted-foreground font-normal">{option.description}</p>
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </FormControl>
            </FormItem>
          )}
        />
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline">
              Cancel
            </Button>
          </DialogClose>
          <Button type="submit" disabled={loading} isLoading={loading}>
            Save permission
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
};

const EditPermissionDialog = () => {
  const { editMembershipId, closeEditDialog } = useMembershipActions();
  const { app } = useApp();

  const membership = useFragment<IAppMembership>({
    fragment: AppMembershipFragment,
    from: { __typename: 'AppMembership', id: editMembershipId ?? '' },
  });

  if (!membership) return null;

  const onOpenChange = (open: boolean) => {
    if (!open) closeEditDialog();
  };

  return (
    <Dialog open={Boolean(editMembershipId)} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit {membership.data.user?.name}&apos;s permission</DialogTitle>
          <DialogDescription>
            They keep access to all of {app.name} — the permission only sets what they can do with it.
          </DialogDescription>
        </DialogHeader>
        {editMembershipId && (
          <EditPermissionForm
            key={editMembershipId}
            membershipId={editMembershipId}
            role={membership.data.role ?? AppMembershipRoleEnum.Viewer}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EditPermissionDialog;
